import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import {
  canSplitStatus,
  FulfillmentGroupStatus,
} from "@redotech/redo-fulfillment-group-common/fulfillment-group-status";
import { RedoShopifyFulfillmentHoldReason } from "@redotech/redo-fulfillment-group-common/fulfillment-hold-reason";
import { Provider } from "@redotech/redo-order-common/provider";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

const attributesForNewFulfillmentGroupsSchema_ = z.object({
  internalNote: z.string().nullish(),
  status: createEnum(FulfillmentGroupStatus),
  holdDetails: z
    .object({
      onHoldUntil: z.date().nullish(),
      reason: createEnum(RedoShopifyFulfillmentHoldReason),
    })
    .nullish(),
});
export const attributesForNewFulfillmentGroupsSchema: z.ZodType<AttributesForNewFulfillmentGroups> =
  attributesForNewFulfillmentGroupsSchema_;
export interface AttributesForNewFulfillmentGroups extends z.infer<
  typeof attributesForNewFulfillmentGroupsSchema_
> {}

const ItemToSplitSchema_ = z.object({
  fulfillmentOrderLineItemId: z.string(),
  quantity: z.number(),
});
export const ItemToSplitSchema: z.ZodType<ItemToSplit> = ItemToSplitSchema_;
export interface ItemToSplit extends z.infer<typeof ItemToSplitSchema_> {}

export function canSplitGroup(fulfillmentGroup: FulfillmentGroupData): {
  canSplit: boolean;
  disabledMessage: string | undefined;
} {
  const totalQuantity = fulfillmentGroup.items.reduce(
    (acc, item) => acc + item.quantity,
    0,
  );

  if (!fulfillmentGroup.teamInfo?._id) {
    return {
      canSplit: false,
      disabledMessage: "Cannot split fulfillment without a team",
    };
  }

  const canSplit =
    totalQuantity > 1 &&
    canSplitStatus(fulfillmentGroup.status) &&
    canSplitProviders(fulfillmentGroup.providers || []);

  return {
    canSplit,
    disabledMessage: getSplitDisabledMessage(
      fulfillmentGroup.status,
      fulfillmentGroup.providers,
      totalQuantity,
    ),
  };
}

// TODO make splitting work for all providers
export function canSplitProviders(providers: Provider[]): boolean {
  const unsupportedProviders = [
    Provider.AMAZON,
    Provider.EBAY,
    Provider.CIN7_OMNI,
    Provider.AIMS360,
    Provider.FAIRE,
    Provider.WOOCOMMERCE,
    Provider.TIKTOK,
    Provider.LINNWORKS_TRACKSTAR,
    Provider.DSCO,
  ];

  if (providers.some((p) => unsupportedProviders.includes(p))) {
    return false;
  }

  return true;
}

export function getSplitDisabledMessage(
  status: FulfillmentGroupStatus | null | undefined,
  providers: Provider[] | null | undefined,
  totalQuantity: number,
): string | undefined {
  if (totalQuantity <= 1) {
    return "Cannot split fulfillment with only 1 item";
  }
  if (!canSplitStatus(status)) {
    return `Cannot split fulfillment that is ${status}`;
  }
  if (!canSplitProviders(providers || [])) {
    return "Splitting fulfillments from this marketplace is not yet supported";
  }
  return undefined;
}
